import React from 'react';
import Dropdown from 'react-dropdown'; // https://www.npmjs.com/package/react-dropdown
import { connect } from 'react-redux';
import { NavLink, withRouter } from 'react-router-dom';
import cn from 'classnames';
import { first, isArray } from 'lodash';
import moment from 'moment';

import saveClient from 'actions/client/saveClient.action';
import { feSidebarOpen } from 'actions/ui.actions';
import validateEmail from 'bl/validation/validateEmail.bl';
import SalonFlag from 'components/core/SalonFlag.component';
import Button from 'components/form/Button.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import PhoneInput from 'components/wrapped/PhoneInput.component';
import IN from 'constants/in';
import LINK from 'constants/link.const';
import SF from 'constants/salonFlag.const';
import SP from 'constants/ui/sidePanel.const';
import { withTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonFlags, selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import { selectSidebarAppointmentId, selectSidebarDetour, selectSidebarPanel } from 'selectors/sidebar.selectors';
import { isEffectiveReceptionist, selectHideDetails } from 'selectors/user';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { fromPropsToName } from 'utils/converters/props/bl.util';
import { fromPropsToPath } from 'utils/converters/props/path.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import updateIntercomElement from 'utils/dom/updateIntercomElement.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import parseJson from 'utils/parsers/parseJson';
import isEmptyArray from 'utils/predicates/array/isEmptyArray.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import { isValidPhone } from 'utils/predicates/isValid.util';
// eslint-disable-line import/first
import isEmptyObject from 'utils/predicates/object/isEmptyObject.util';

import BackButton from 'booking/components/common/bw-header/BackButton';
import BWContent from 'booking/components/common/BWContent';
import BWFooter from 'booking/components/common/BWFooter';
import PageTitle from 'booking/components/common/PageTitle';
import TextField from 'booking/components/common/TextField';
import ClientTagPicker from 'clientProfile/components/BasicProfile/ClientTagPicker';
import TagsLayout from 'clientProfile/components/BasicProfile/TagsLayout';
import DummyBannerImage from 'clientProfile/components/common/DummyBannerImage.component';
import { checkIfDatesAreCorrect, getDayOptionsForMonth } from 'clientProfile/utils';
import { validatePhone } from 'lib/validators';
import { getUserPermission } from 'salon/actions';

import 'react-dropdown/style.css';

const L = logger('EditDetailsContainer');

const STYLE_ERROR = Object.freeze({
  color: '#D32F2F',
  fontSize: '12px',
  textAlign: 'left',
  display: 'block',
  paddingTop: '8px',
  clear: 'both',
});

const translateDayOptions = (t) => getDayOptionsForMonth(1, t);

const translateMonthOptions = (t) =>
  Object.freeze([
    { value: null, label: t('scheduler.newClient.monthsDropdown.label') },
    { value: 1, label: t('scheduler.newClient.monthsDropdown.january') },
    { value: 2, label: t('scheduler.newClient.monthsDropdown.february') },
    { value: 3, label: t('scheduler.newClient.monthsDropdown.march') },
    { value: 4, label: t('scheduler.newClient.monthsDropdown.april') },
    { value: 5, label: t('scheduler.newClient.monthsDropdown.may') },
    { value: 6, label: t('scheduler.newClient.monthsDropdown.june') },
    { value: 7, label: t('scheduler.newClient.monthsDropdown.july') },
    { value: 8, label: t('scheduler.newClient.monthsDropdown.august') },
    { value: 9, label: t('scheduler.newClient.monthsDropdown.september') },
    { value: 10, label: t('scheduler.newClient.monthsDropdown.october') },
    { value: 11, label: t('scheduler.newClient.monthsDropdown.november') },
    { value: 12, label: t('scheduler.newClient.monthsDropdown.december') },
  ]);

const determineChosenDay = (birthday, t) => {
  if (!birthday || !t) {
    return null;
  }

  const [day] = birthday;
  return translateDayOptions(t).find(({ value }) => value === day) || null;
};

const determineChosenMonth = (birthday, t) => {
  if (!birthday || !t) {
    return null;
  }

  const [, month] = birthday;
  return translateMonthOptions(t).find(({ value }) => value === month) || null;
};

const compareAndAddClicked = (arr1, arr2) => {
  if (isEmptyArray(arr1)) return [];
  if (!isFullArray(arr2)) return arr1.map((o) => ({ ...o, clicked: false }));
  return arr1.map((o) => (arr2.some((o2) => o.id === o2.id) ? { ...o, clicked: true } : { ...o, clicked: false }));
};

class EditDetails extends React.Component {
  constructor(props) {
    super(props);
    const { client, clientTags, new: isNew, salonTags, t } = props;
    const { birthday, birthyear } = client || {};
    this.state = {
      alternativePhoneErrorMessage: '',
      chosenDay: client && !isNew ? determineChosenDay(birthday, t) : null,
      chosenMonth: client && !isNew ? determineChosenMonth(birthday, t) : null,
      chosenYear: client && birthyear ? fromAnyToInteger(birthyear) : null,
      clientProps: { ...client, birthyear: birthyear || null },
      dateError: '',
      daysOptions: translateDayOptions(t),
      emailErrorMessage: '',
      errorLimitTag: false,
      inBookingWindow: false,
      isNewClient: null,
      nameErrorMessage: '',
      selectedClientTags: salonTags && clientTags ? compareAndAddClicked(salonTags, clientTags) : [],
      selectedTagsCount: clientTags && !isNew && isArray(clientTags) ? clientTags.length : 0,
      startedFetch: false,
      toggleTagContainer: true,
      yearErrorMessage: '',
    };
  }

  UNSAFE_componentWillMount() {
    const { new: isNew, getUserPermission, salonId, saveLocalTags } = this.props;
    const { clientProps, selectedClientTags } = this.state;
    const name = fromPropsToName(this.props);

    updateIntercomElement({ show: false, tag: 'EditDetails.UNSAFE_componentWillMount()' });

    if (name) {
      this.setState({ clientProps: { ...clientProps, user_name: name } });
    }

    if (isEmptyObject(clientProps)) {
      // clientProps is empty object
      this.setState({
        selectedClientTags: selectedClientTags.map((tag) => ({
          ...tag,
          clicked: false,
        })),
      });
    }

    if (isNew) {
      L.unignore('componentWillMount()', 'saveLocalTags:', saveLocalTags([]));
    }

    L.unignore('componentWillMount()', 'getUserPermission:', getUserPermission(salonId));
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { clientProps, startedFetch } = this.state;
    const { new: isNew, location, history, client: prevClient } = this.props;
    const { newClient, client: nextClient, isFetching, pickClient, salonTags, clientTags } = nextProps;
    const { startedFetch: clientStartedFetch, isFetching: clientIsFetching, response, errorMessage } = newClient || {};
    const oldPath = fromPropsToPath(this.props);
    const newPath = fromPropsToPath(nextProps);
    const segments = location ? oldPath.split('/') : [];

    if (nextClient) {
      this.setState({ clientProps: { ...nextClient, birthyear: nextClient.birthyear || null } });
    }

    if (salonTags && clientTags) {
      this.setState({ selectedClientTags: compareAndAddClicked(salonTags, clientTags) });
      this.setState({ selectedTagsCount: isArray(clientTags) ? clientTags.length : 0 });
    }

    if (!location) {
      if (startedFetch && isFetching && !isFetching) {
        history.goBack();
      }
      return;
    }

    // Are we embedded within the appointment window?
    // If so set local state so that close and save buttons can do the right thing and hide the "delete" button
    const inBookingWindow = fromAnyToBool(newPath.includes('/appointment/'));
    this.setState({ inBookingWindow });

    if (oldPath.indexOf('appointment') >= 0 && newClient) {
      if (startedFetch && !clientIsFetching && clientStartedFetch && errorMessage === '') {
        pickClient(response);
        const index = segments.indexOf('new');
        const chopped = segments.slice(0, index).join('/');
        const subPath = oldPath.indexOf('new') >= 0 ? '/new/services' : '/summary';
        const link = `${chopped}${subPath}`;
        history.push(link);
      }
    } else if (startedFetch) {
      if (isNew) {
        if (startedFetch && !clientIsFetching && clientStartedFetch && errorMessage === '') {
          const responseId = response.id;
          const index = segments.indexOf('create');
          const chopped = segments.slice(0, index).join('/');
          const link = `${chopped}/client/${responseId}`;
          L.debug('componentWillReceiveProps()', 'Go to link:', link, { index, chopped, responseId, isNew });
          history.push(link);
        }
      } else if (prevClient) {
        if (inBookingWindow) {
          L.debug('componentWillReceiveProps()', 'Go back to previous since inBookingWindow:', inBookingWindow);
          history.goBack();
        } else {
          const index = segments.indexOf('edit');
          const chopped = segments.slice(0, index).join('/');
          const clientId = fromAnyToString(prevClient && prevClient.id);
          const link = `${chopped}/${clientId}`;
          L.debug('componentWillReceiveProps()', 'Go to link:', link, { index, chopped, prevClient });
          history.push(link);
        }
      } else {
        L.debug('componentWillReceiveProps()', 'Go back to previous as a fallback');
        history.goBack();
      }
    }

    const userName = oldPath.split('new/client/new/')[1];
    if (userName) {
      clientProps.user_name = userName;
      this.setState({ clientProps });
    }

    this.setState({
      isNewClient: fromAnyToBool(
        prevClient &&
          !prevClient.id &&
          (oldPath.indexOf('new/client') > 0 || oldPath.indexOf('appointment/new/create/') > 0),
      ),
    });
  }

  componentWillUnmount() {
    this.setState({ selectedClientTags: [] });
    updateIntercomElement({ show: true, tag: 'EditDetails.componentWillUnmount()' });
  }

  save = () => this.onSave(); // TODO: just use onSave instead of save, check if tests and/or parent components require save to exist

  backToVoucher = (newClientName) => {
    const { location, history } = this.props;
    const { fromVoucher, isPurchaser, isRecipient } = location.state;
    history.push({ pathname: fromVoucher, state: { newClientName, isRecipient, isPurchaser } });
  };

  validateNameSymbol = (value) => {
    this.setState({ clientProps: { ...this.state.clientProps, user_name: value } });
  };

  // FIXME: this is a bad implementation, will fix it in future with chosenYear

  validateEmailSymbol = (value) => {
    const { clientProps } = this.state;

    this.setState({ clientProps: { ...clientProps, email: value } });
  };

  validateName = (name) => {
    if (name.length > 0) {
      this.setState({ nameErrorMessage: '' });
    } else {
      this.setState({ nameErrorMessage: this.props.t('scheduler.newClient.errors.clientName') });
    }
  };

  validatePhoneNumber = (phone, type) => {
    const { clientProps } = this.state;

    if (!phone) {
      if (type === 'normal') {
        this.setState({ clientProps: { ...clientProps, phone: '' } });
      } else if (type === 'alternative') {
        this.setState({
          clientProps: { ...clientProps, alternative_phone: '' },
          alternativePhoneErrorMessage: '',
        });
      }
      return;
    }
    if (typeof phone === 'object') {
      // eslint-disable-next-line no-param-reassign
      phone = phone.target.value;
    }
    if (validatePhone(phone)) {
      if (type === 'normal') {
        this.setState({ clientProps: { ...clientProps, phone } });
      } else if (type === 'alternative') {
        this.setState({
          clientProps: { ...clientProps, alternative_phone: phone },
          alternativePhoneErrorMessage: '',
        });
      }
    } else if (type === 'normal') {
      this.setState({ clientProps: { ...clientProps, phone } });
    } else if (type === 'alternative') {
      this.setState({ clientProps: { ...clientProps, alternative_phone: phone } });
      this.setState({ alternativePhoneErrorMessage: this.props.t('scheduler.newClient.errors.alternativeNumber') });
    }
  };

  validateEmail = (value) => {
    if (value.length === 0 || validateEmail({ value })) {
      this.setState({ emailErrorMessage: '' });
    } else {
      this.setState({ emailErrorMessage: this.props.t('scheduler.newClient.errors.email') });
    }
  };

  handleSelectDate = (chosenDay) => {
    const { chosenMonth, dateError } = this.state;
    const stateChanger = { chosenDay, dateError };

    if (dateError && checkIfDatesAreCorrect(stateChanger.chosenDay, chosenMonth)) {
      stateChanger.dateError = '';
    }
    this.setState(stateChanger);
  };

  handleSelectMonth = (chosenMonth) => {
    const { chosenDay, dateError } = this.state;

    const availableDays = getDayOptionsForMonth(chosenMonth.value || 0);
    const stateChanger = {
      chosenMonth,
      daysOptions: availableDays,
      chosenDay,
      dateError,
    };
    if (!chosenDay || !availableDays.filter((day) => chosenDay && day.value === chosenDay.value).length) {
      stateChanger.chosenDay = null;
    }

    if (dateError && checkIfDatesAreCorrect(stateChanger.chosenDay, stateChanger.chosenMonth)) {
      stateChanger.dateError = '';
    }

    this.setState(stateChanger);
  };

  handleSelectYear = ({ target }) => {
    const { clientProps } = this.state;
    const year = target.value;

    const result = year && year - Math.floor(year) !== 0;

    this.setState({ dateError: '' });

    if (result) {
      this.setState({ yearErrorMessage: this.props.t('scheduler.newClient.errors.invalidYear') });
    } else {
      this.setState({
        yearErrorMessage:
          year && (year < 1900 || year >= moment().get('year'))
            ? this.props.t('scheduler.newClient.errors.wrongYear', { currentYear: moment().get('year') })
            : '',
        clientProps: { ...clientProps, birthyear: year || null },
        chosenYear: year || null,
      });
    }
  };

  confirmSelectionItems = (modifiedClientTags) => {
    const { saveLocalTags } = this.props;

    const newList = [];

    const filteredTags = [...modifiedClientTags.filter(({ clicked }) => clicked === true)];

    for (let i = 0; i < filteredTags.length; i += 1) {
      const item = parseJson(JSON.stringify(filteredTags[i]));
      newList.push(item);
    }

    if (filteredTags.length < 3) {
      this.setState({ errorLimitTag: false });
    }

    this.setState({ selectedTagsCount: filteredTags.length });

    L.unignore('confirmSelectionItems()', 'saveLocalTags:', saveLocalTags(newList));
  };

  handleClientAddressChange = (e) =>
    void this.setState({
      clientProps: {
        ...this.state.clientProps,
        address: e.target.value,
      },
    });

  handleClientNoteChange = (e) =>
    void this.setState({
      clientProps: {
        ...this.state.clientProps,
        notes: e.target.value,
      },
    });

  handleKnownAsChange = (e) =>
    void this.setState({
      clientProps: {
        ...this.state.clientProps,
        known_as: e.target.value,
      },
    });

  handleClientTagSelect = (itemId) => {
    const { selectedClientTags } = this.state;
    const modifiedItemList = selectedClientTags.map((tag) =>
      tag.id === itemId ? { ...tag, clicked: !tag.clicked } : tag,
    );
    const matchingItems = selectedClientTags.filter(({ id }) => id === itemId);
    const firstMatch = first(matchingItems);
    if (firstMatch) {
      const clientCount = selectedClientTags.filter(({ clicked }) => clicked === true).length;
      if (clientCount < 3 || firstMatch.clicked) {
        firstMatch.clicked = !firstMatch.clicked;
        this.confirmSelectionItems(modifiedItemList);
        this.setState({ selectedClientTags: modifiedItemList });
        return;
      }
      if (clientCount === 3) {
        this.setState({ errorLimitTag: true });
      }
    }
    this.confirmSelectionItems(selectedClientTags);
  };

  toggleTags = () => void this.setState({ toggleTagContainer: !this.state.toggleTagContainer });

  handleBannerClick = () => {
    const { salonId } = this.props;
    navFor('EditDetailsContainer.handleBannerClick()', this.props).push(
      linkTo({
        pattern: LINK.slnTags,
        params: { salonId },
      }),
    );
  };

  onSave = async () => {
    const {
      client,
      location: { pathname, state: locationState },
      saveClient,
      history,
      createNewClient,
      salonId,
      appointmentId,
    } = this.props;

    const {
      alternativePhoneErrorMessage,
      chosenDay,
      chosenMonth,
      chosenYear,
      clientProps,
      emailErrorMessage,
      isNewClient,
      selectedClientTags,
      yearErrorMessage,
    } = this.state;

    const {
      birthyear,
      address,
      phone,
      email,
      known_as: knownAs,
      notes,
      alternative_phone: altPhone,
      user_name: userName,
    } = clientProps || {};

    const determinedYear = birthyear || chosenYear;

    if (yearErrorMessage || !checkIfDatesAreCorrect(chosenDay, chosenMonth)) {
      this.setState({ dateError: this.props.t('scheduler.newClient.errors.wrongDates') });
      return;
    } else if (((determinedYear && !chosenDay) || (determinedYear && !chosenMonth)) && !isNewClient) {
      this.setState({ dateError: this.props.t('scheduler.newClient.errors.selectDates') });
      return;
    }

    const birthday =
      chosenDay && chosenMonth && chosenDay.value && chosenMonth.value ? [chosenDay.value, chosenMonth.value] : null;

    const clientTags = selectedClientTags
      ? selectedClientTags.filter(({ clicked }) => clicked === true).map((tag) => ({ id: tag.id }))
      : null;

    const hasErrorMessage = fromAnyToBool(alternativePhoneErrorMessage || emailErrorMessage || yearErrorMessage);

    const clientId = client && client.id;
    try {
      if (!hasErrorMessage && clientId) {
        const newClientProps = {};
        if (chosenYear) newClientProps.birthyear = chosenYear;
        if (birthday) newClientProps.birthday = birthday;
        if (clientTags && clientTags.length) newClientProps.client_tags = clientTags;
        if (phone === undefined) newClientProps.phone = '';

        if (!isEmptyObject(newClientProps)) this.setState({ clientProps: { ...clientProps, ...newClientProps } });

        const data = { ...clientProps, birthday, birthyear: determinedYear || null, client_tags: clientTags };

        await saveClient(salonId, data);
      } else {
        this.setState({ startedFetch: true });

        await createNewClient({
          user_name: userName,
          email,
          phone,
          alternative_phone: altPhone,
          known_as: knownAs,
          notes,
          birthday,
          birthyear: determinedYear || null,
          salon_id: salonId,
          client_tags: clientTags,
          address,
        });
      }
    } catch (e) {
      L.warn('onSave()', 'error:', e);
    }

    if (appointmentId) {
      if (pathname.includes('/client/edit/')) {
        L.debug('onSave()', 'Exiting client edit and navigating back...');
        const newPath = pathname.split('/client/edit/')[0];
        history.push(newPath);
      }

      this.props.feSidebarOpen({
        panel: SP.appointmentDefault,
        appointmentId,
        tag: 'EditDetailContainer.onSave()',
      });
    } else {
      if (pathname.includes('/client/edit/')) {
        // after saved reset phone
        this.setState({ clientProps: { ...clientProps, phone: undefined } });
        history.goBack();
      }

      if (locationState && locationState.fromVoucher) {
        this.backToVoucher(userName);
      }
    }
  };

  stayOnCurrentAppointment = () => {
    const {
      appointmentId,
      location: { pathname, state: locationState },
      history,
    } = this.props;

    if (appointmentId) {
      if (pathname.includes('/client/edit/')) {
        const newPath = pathname.split('/client/edit/')[0];
        history.push(newPath);
      } else if (!locationState || !locationState.fromVoucher) {
        history.goBack();
      } else {
        this.backToVoucher(''); // ?
      }

      this.props.feSidebarOpen({
        panel: SP.appointmentDefault,
        appointmentId,
        tag: 'EditDetailContainer.stayOnCurrentAppointment()',
      });
    } else {
      history.goBack();
    }
  };

  render() {
    const { deleteClient, hideDetails, isFetching, isReceptionist, salonId, new: isNew, countryFlag, t } = this.props;
    const {
      alternativePhoneErrorMessage,
      chosenDay,
      chosenMonth,
      clientProps: {
        address,
        alternative_phone: clientAltPhone,
        birthyear,
        email,
        known_as: knownAs,
        notes,
        phone: clientPhone,
        user_name: clientName,
      },
      dateError,
      daysOptions,
      emailErrorMessage,
      errorLimitTag,
      inBookingWindow,
      nameErrorMessage,
      selectedClientTags,
      selectedTagsCount,
      toggleTagContainer,
      yearErrorMessage,
    } = this.state;
    const defaultDayOption = daysOptions[0];
    const pageName = isNew ? t('scheduler.newClient.addNewClient') : t('scheduler.newClient.editClientDetails');
    const saveBtnName = isNew ? t('scheduler.newClient.saveNewClient') : t('scheduler.newClient.saveChanges');

    const toggleClass = cn({
      'tags-container': true,
      expanded: toggleTagContainer,
      closed: !toggleTagContainer,
    });

    const addEditClass = cn({
      btn: true,
      'btn-blue': true,
      'show-edit-class': selectedClientTags.length > 4 && toggleTagContainer,
      'hide-edit-class': selectedClientTags.length > 4 && !toggleTagContainer,
    });

    const disableSaveButton =
      (clientPhone && !isValidPhone({ value: clientPhone })) ||
      !clientName ||
      alternativePhoneErrorMessage ||
      emailErrorMessage;

    const buttonPanelClasses = cn({
      relative: true,
      'btns-to-left': true,
    });

    const MONTH_OPTIONS = translateMonthOptions(t);

    return (
      <div data-bem="EditDetailsContainer" className="client-edit">
        <PageTitle pageTitle={pageName} subTitle="" />

        <BackButton onClick={this.stayOnCurrentAppointment} />

        <BWContent additionalClass="space">
          <div data-bem="EditDetailsContainer__bw-content" style={{ paddingTop: 35 }}>
            <div>
              <TextField
                tag="EditDetailsContainer__client-name"
                labelName={t('scheduler.newClient.clientNameLabel')}
                htmlFor="name"
              >
                <div className="tags-container">
                  <input
                    id="name"
                    className="input-default"
                    type="text"
                    onChange={(e) => this.validateNameSymbol(e.target.value)}
                    onBlur={(e) => this.validateName(e.target.value)}
                    value={fromAnyToString(clientName).replace(/\s{2,}/g, ' ')}
                    placeholder={t('scheduler.newClient.clientNamePlaceholder')}
                    autoComplete="off"
                  />
                  {nameErrorMessage && <span style={STYLE_ERROR}>{nameErrorMessage}</span>}
                </div>
              </TextField>
              <SalonFlag name={SF.clientTags}>
                <SalonFlag.IfDisabled>
                  <DummyBannerImage show onClick={this.handleBannerClick}>
                    <div>{t('scheduler.newClient.clientTagsMessage')}</div>
                  </DummyBannerImage>
                </SalonFlag.IfDisabled>
                <SalonFlag.IfEnabled>
                  <div>
                    <span className="tags-selected-count">
                      {t('scheduler.newClient.selectedTags', { tagsCount: selectedTagsCount })}
                    </span>
                    {fromAnyToBool(errorLimitTag) && (
                      <span className="error-mess">{t('scheduler.newClient.maximumTags')}</span>
                    )}
                    <div className={toggleClass}>
                      <TagsLayout aditionalClass="like-button">
                        <ClientTagPicker
                          clientTags={selectedClientTags}
                          itemClicked={this.handleClientTagSelect}
                          showHideTag={toggleTagContainer}
                        >
                          <NavLink
                            className={addEditClass}
                            to={linkTo({
                              pattern: LINK.slnTags,
                              params: { salonId },
                              fault: '#',
                            })}
                          >
                            {t('scheduler.newClient.tagsLink')}
                          </NavLink>
                        </ClientTagPicker>
                      </TagsLayout>
                    </div>
                    {selectedClientTags.length > 4 && (
                      <div className="view-more-less-div">
                        <button onClick={this.toggleTags} className="view-more-less">
                          {toggleTagContainer ? t('scheduler.newClient.hideTags') : t('scheduler.newClient.showTags')}
                        </button>
                      </div>
                    )}
                  </div>
                </SalonFlag.IfEnabled>
              </SalonFlag>

              <TextField
                tag="EditDetailsContainer__client-nick-name"
                labelName={t('scheduler.newClient.reminderNameLabel')}
                htmlFor="known_as"
              >
                <input
                  id="known_as"
                  className="input-default"
                  type="text"
                  onChange={(e) => this.handleKnownAsChange(e)}
                  onBlur={(e) => this.handleKnownAsChange(e)}
                  value={knownAs || ''}
                  placeholder={t('scheduler.newClient.reminderNamePlaceholder')}
                  autoComplete="off"
                />
              </TextField>
              <TextField
                tag="EditDetailsContainer__client-phone"
                labelName={t('scheduler.newClient.mobileNumberLabel')}
                htmlFor="phone"
              >
                <div className={`cfix relative ${!isNew && hideDetails ? 'blurInternPhone' : ' '}`}>
                  <PhoneInput
                    international
                    defaultCountry={countryFlag}
                    placeholder={t('scheduler.newClient.mobileNumberPlaceholder')}
                    value={clientPhone}
                    onChange={(value) => {
                      this.validatePhoneNumber(value, 'normal');
                    }}
                  />

                  {!clientPhone ? (
                    ''
                  ) : (
                    <span style={STYLE_ERROR}>
                      {(clientPhone && isValidPhone({ value: clientPhone })) || !clientPhone
                        ? ''
                        : t('scheduler.newClient.errors.mobileNumber')}
                    </span>
                  )}

                  {!isNew && <TooltipForReceptionist where={IN.clientPhone} checkDetails />}
                </div>
              </TextField>

              <TextField
                tag="EditDetailsContainer__client-alt-phone"
                labelName={t('scheduler.newClient.alternativeNumberLabel')}
                htmlFor="alternative_phone"
              >
                <div className="cfix relative">
                  <input
                    id="alternative_phone"
                    className={!isNew && hideDetails ? 'input-default blur' : 'input-default '}
                    type="tel"
                    placeholder={t('scheduler.newClient.alternativeNumberPlaceholder')}
                    onChange={
                      !isNew && hideDetails
                        ? () => {}
                        : (e) => {
                            this.validatePhoneNumber(e.target.value, 'alternative');
                          }
                    }
                    value={fromAnyToString(clientAltPhone)}
                    autoComplete="off"
                    readOnly={!isNew && hideDetails}
                  />

                  {alternativePhoneErrorMessage && <span style={STYLE_ERROR}>{alternativePhoneErrorMessage}</span>}
                  {!isNew && <TooltipForReceptionist where={IN.clientPhone} checkDetails />}
                </div>
              </TextField>
              <TextField
                tag="EditDetailsContainer__client-email"
                labelName={t('scheduler.newClient.emailLabel')}
                htmlFor="email"
              >
                <div className="cfix relative">
                  <input
                    id="email"
                    className={!isNew && hideDetails ? 'input-default blur' : 'input-default '}
                    type="text"
                    onChange={(e) => void this.validateEmailSymbol(e.target.value)}
                    onBlur={(e) => void this.validateEmail(e.target.value)}
                    value={fromAnyToString(email)}
                    placeholder={t('scheduler.newClient.emailPlaceholder')}
                    autoComplete="off"
                    readOnly={!isNew && hideDetails}
                  />

                  {emailErrorMessage && <span style={STYLE_ERROR}>{emailErrorMessage}</span>}
                  {!isNew && <TooltipForReceptionist where={IN.clientPhone} checkDetails />}
                </div>
              </TextField>
              <TextField
                tag="EditDetailsContainer__client-notes"
                labelName={t('scheduler.newClient.clientNotesLabel')}
                htmlFor="notes"
              >
                <textarea
                  style={{ resize: 'vertical' }}
                  onChange={(e) => this.handleClientNoteChange(e)}
                  value={notes}
                  placeholder=""
                  onBlur={(e) => this.handleClientNoteChange(e)}
                />
              </TextField>
              <TextField
                tag="EditDetailsContainer__client-addr"
                labelName={t('scheduler.newClient.clientAddressLabel')}
                htmlFor="address"
              >
                <textarea
                  style={{ resize: 'vertical' }}
                  onChange={(e) => this.handleClientAddressChange(e)}
                  onBlur={(e) => this.handleClientAddressChange(e)}
                  value={fromAnyToString(address)}
                  placeholder=""
                />
              </TextField>
              <div className="cfix">
                <TextField
                  tag="EditDetailsContainer__client-birth"
                  labelName={t('scheduler.newClient.dateOfBirthLabel')}
                  htmlFor="date"
                >
                  <Dropdown
                    className="day"
                    controlClassName="react-dropdown"
                    menuClassName="display-top"
                    options={daysOptions}
                    onChange={this.handleSelectDate}
                    value={chosenDay || defaultDayOption}
                    placeholder={t('scheduler.newClient.dayPlaceholder')}
                  />
                  <Dropdown
                    className="month"
                    controlClassName="react-dropdown"
                    menuClassName="display-top"
                    options={MONTH_OPTIONS}
                    onChange={this.handleSelectMonth}
                    value={chosenMonth || MONTH_OPTIONS[0]}
                    placeholder={t('scheduler.newClient.monthPlaceholder')}
                  />

                  <input
                    id="birthyear"
                    className="input-default-birthyear"
                    type="number"
                    onChange={this.handleSelectYear}
                    value={birthyear || ''}
                    placeholder={t('scheduler.newClient.yearPlaceholder')}
                    autoComplete="off"
                  />
                  {yearErrorMessage && <span style={STYLE_ERROR}>{yearErrorMessage}</span>}
                  {dateError && <span style={STYLE_ERROR}>{dateError}</span>}
                </TextField>
              </div>
              <div style={{ height: 220, width: 220 }} />
            </div>
          </div>
        </BWContent>

        <BWFooter additionalClass="apointment-footer border-t d-end">
          <div data-bem="EditDetailsContainer__button-panel" className={buttonPanelClasses}>
            <Button
              tag="EditDetailsContainer__save-client-button"
              variant="primary"
              onClick={this.save}
              disabled={isFetching || disableSaveButton}
              xSpace
            >
              {saveBtnName}
            </Button>

            {!isNew && !inBookingWindow && (
              <div>
                {isReceptionist ? (
                  <div>
                    <Button tag="EditDetailsContainer__delete-client-button" variant="slim">
                      {t('scheduler.newClient.deleteClientButton')}
                    </Button>
                    <TooltipForReceptionist where={IN.btnDeleteClient} />
                  </div>
                ) : (
                  <Button tag="EditDetailsContainer__delete-client-button" variant="slim" onClick={deleteClient}>
                    {t('scheduler.newClient.deleteClientButton')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </BWFooter>
      </div>
    );
  }
}

const EditDetailsHoc = connect(
  (state) => ({
    salonId: selectCurrentSalonId(state),
    hideDetails: selectHideDetails(state),
    isReceptionist: isEffectiveReceptionist(state),
    countryFlag: getAnyOrVoid('currentSalon.country.code', state),
    appointmentId: selectSidebarAppointmentId(state),
    panel: selectSidebarPanel(state),
    isSidebarDetour: selectSidebarDetour(state),
    salonFlags: selectCurrentSalonFlags(state),
  }),
  mapActionsToDispatchToProps({
    getUserPermission,
    saveClient,
    feSidebarOpen,
  }),
)(withRouter(withTranslation()(EditDetails)));

export default EditDetailsHoc;
